import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Card,
    Row,
    Col,
    Statistic,
    Select,
    Space,
    Typography,
    Table,
    Empty,
    Spin,
    message,
    Divider,
    DatePicker,
    Tag
} from 'antd';
import {
    LineChartOutlined,
    SearchOutlined,
    GlobalOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import dnsService from '../../services/dnsService';
import dayjs from 'dayjs';
import WorldMap from '../../../shared/components/WorldMap';
import { CountryLabel } from '../../../shared/components/CountryFlag';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// DNS record type names
const QTYPE_NAMES = {
    1: 'A',
    2: 'NS',
    5: 'CNAME',
    6: 'SOA',
    12: 'PTR',
    15: 'MX',
    16: 'TXT',
    28: 'AAAA',
    33: 'SRV',
    43: 'DS',
    46: 'RRSIG',
    47: 'NSEC',
    48: 'DNSKEY',
    257: 'CAA'
};

// DNS response code names
const RCODE_NAMES = {
    0: 'NOERROR',
    1: 'FORMERR',
    2: 'SERVFAIL',
    3: 'NXDOMAIN',
    4: 'NOTIMP',
    5: 'REFUSED'
};

const DnsAnalytics = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(24, 'hour'),
        dayjs()
    ]);

    // Zone filter
    const [selectedZone, setSelectedZone] = useState(null);
    const [zones, setZones] = useState([]);

    const [stats, setStats] = useState({
        total_queries: 0,
        unique_qnames: 0,
        avg_response_time: 0,
        rcode_distribution: {},
        topQnames: [],
        qtypeDistribution: [],
        geoDistribution: [],
        queryTrend: []
    });

    useEffect(() => {
        loadZones();
    }, []);

    // zone 加载后确保选中一个 zone：URL 的 ?zone_id 优先，否则默认第一个。
    // 后端 analytics 强制要求指定 zone（否则 403），所以必须先有 zone 才能查。
    useEffect(() => {
        if (zones.length === 0) return;
        const zoneIdParam = searchParams.get('zone_id');
        if (zoneIdParam) {
            const zoneId = parseInt(zoneIdParam, 10);
            if (zones.some(z => Number(z.id) === zoneId)) {
                setSelectedZone(zoneId);
                return;
            }
        }
        setSelectedZone(prev => (prev != null ? prev : zones[0].id));
    }, [searchParams, zones]);

    useEffect(() => {
        // 未选定 zone 前不调用（避免 mount 时无 zone → 后端 403 "Zone not found or access denied"）
        if (dateRange && dateRange[0] && dateRange[1] && selectedZone) {
            loadAllStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, selectedZone]);

    const loadZones = async () => {
        try {
            const result = await dnsService.getDnsZones();
            if (result.success) {
                setZones(result.data || []);
            }
        } catch (error) {
            console.error('Failed to load zones:', error);
        }
    };

    const loadAllStats = async () => {
        setLoading(true);
        try {
            const startTime = dateRange[0].unix();
            const endTime = dateRange[1].unix();
            const params = { start_time: startTime, end_time: endTime };

            if (selectedZone) {
                const zone = zones.find(z => Number(z.id) === Number(selectedZone));
                if (zone) {
                    params.zone = zone.zone;
                }
            }

            const [statsRes, topQnamesRes, qtypeRes, geoRes, trendRes] = await Promise.all([
                dnsService.getAnalytics(params),
                dnsService.getTopQnames({ ...params, limit: 20 }),
                dnsService.getQtypeDistribution(params),
                dnsService.getGeoDistribution(params),
                dnsService.getQueryTrend({ ...params, interval: '1h' })
            ]);

            setStats({
                total_queries: statsRes.success ? statsRes.data?.total_queries || 0 : 0,
                unique_qnames: statsRes.success ? statsRes.data?.unique_qnames || 0 : 0,
                avg_response_time: statsRes.success ? statsRes.data?.avg_response_time || 0 : 0,
                rcode_distribution: statsRes.success ? statsRes.data?.rcode_distribution || {} : {},
                topQnames: topQnamesRes.success ? topQnamesRes.data || [] : [],
                qtypeDistribution: qtypeRes.success ? qtypeRes.data || [] : [],
                geoDistribution: geoRes.success ? geoRes.data || [] : [],
                queryTrend: trendRes.success ? trendRes.data || [] : []
            });
        } catch (error) {
            message.error(t('Failed to load statistics: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('zh-CN').format(num || 0);
    };

    // Calculate success rate from rcode distribution
    const getSuccessRate = () => {
        const total = Object.values(stats.rcode_distribution).reduce((a, b) => a + b, 0);
        if (total === 0) return 0;
        const success = stats.rcode_distribution['NOERROR'] || stats.rcode_distribution[0] || 0;
        return ((success / total) * 100).toFixed(2);
    };

    const topQnamesColumns = [
        {
            title: t('Query name'),
            dataIndex: 'qname',
            key: 'qname',
            ellipsis: true,
            render: (text) => <Text code>{text}</Text>
        },
        {
            title: t('Queries'),
            dataIndex: 'count',
            key: 'count',
            width: 120,
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.count - b.count
        }
    ];

    const qtypeColumns = [
        {
            title: t('Record type'),
            dataIndex: 'qtype',
            key: 'qtype',
            render: (qtype) => (
                <Tag color="blue">{QTYPE_NAMES[qtype] || qtype}</Tag>
            )
        },
        {
            title: t('Queries'),
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.count - b.count
        }
    ];

    const geoColumns = [
        {
            title: t('Country/Region'),
            dataIndex: 'country',
            key: 'country',
            render: (text) => (text ? <CountryLabel code={text}>{text}</CountryLabel> : t('Unknown'))
        },
        {
            title: t('Queries'),
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.count - b.count
        }
    ];

    const rcodeColumns = [
        {
            title: t('Response code'),
            dataIndex: 'rcode',
            key: 'rcode',
            render: (rcode) => {
                const name = RCODE_NAMES[rcode] || rcode;
                const color = rcode === 0 || rcode === 'NOERROR' ? 'success' :
                             rcode === 3 || rcode === 'NXDOMAIN' ? 'warning' : 'error';
                return <Tag color={color}>{name}</Tag>;
            }
        },
        {
            title: t('Count'),
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value)
        }
    ];

    // Convert rcode_distribution object to array for table
    const rcodeData = Object.entries(stats.rcode_distribution).map(([rcode, count]) => ({
        rcode,
        count
    }));

    return (
        <div>
            <Card
                title={t('DNS query analytics')}
                extra={
                    <Space>
                        <Select
                            showSearch
                            allowClear
                            style={{ width: 250 }}
                            placeholder={t('Filter domains (all by default)')}
                            value={selectedZone}
                            onChange={setSelectedZone}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {zones.map(zone => (
                                <Option key={zone.id} value={zone.id}>
                                    {zone.display_zone || zone.zone}
                                </Option>
                            ))}
                        </Select>
                        <RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            showTime
                            format="YYYY-MM-DD HH:mm:ss"
                            placeholder={[t('Start time'), t('End time')]}
                        />
                    </Space>
                }
            >
                <Spin spinning={loading}>
                    {/* 统计卡片 */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title={t('Total queries')}
                                    value={formatNumber(stats.total_queries)}
                                    prefix={<SearchOutlined />}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title={t('Unique domains')}
                                    value={formatNumber(stats.unique_qnames)}
                                    prefix={<GlobalOutlined />}
                                    valueStyle={{ color: '#722ed1' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title={t('Success rate')}
                                    value={getSuccessRate()}
                                    suffix="%"
                                    prefix={getSuccessRate() >= 95 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                    valueStyle={{ color: getSuccessRate() >= 95 ? '#3f8600' : '#cf1322' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title={t('Average response time')}
                                    value={stats.avg_response_time}
                                    suffix="ms"
                                    prefix={<LineChartOutlined />}
                                    valueStyle={{ color: stats.avg_response_time < 100 ? '#3f8600' : '#cf1322' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Divider />

                    {/* 详细统计表格 */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <Card title={t('Top queried domains')} size="small">
                                {stats.topQnames.length > 0 ? (
                                    <Table
                                        columns={topQnamesColumns}
                                        dataSource={stats.topQnames}
                                        rowKey="qname"
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description={t('No data yet')} />
                                )}
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card title={t('Query type distribution')} size="small">
                                {stats.qtypeDistribution.length > 0 ? (
                                    <Table
                                        columns={qtypeColumns}
                                        dataSource={stats.qtypeDistribution}
                                        rowKey="qtype"
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description={t('No data yet')} />
                                )}
                            </Card>
                        </Col>

                        {/* 世界地图 - 全宽显示 */}
                        <Col xs={24}>
                            <Card title={t('Global query distribution')} size="small">
                                {stats.geoDistribution.length > 0 ? (
                                    <WorldMap
                                        data={stats.geoDistribution.map(item => ({
                                            country_code: item.country,
                                            country_name: item.country,
                                            requests: item.count
                                        }))}
                                        title=""
                                        valueField="requests"
                                        valueLabel={t('DNS queries')}
                                        colorRange={['#e6f7ff', '#91d5ff', '#69c0ff', '#40a9ff', '#1890ff']}
                                        height={450}
                                        showScatter={false}
                                    />
                                ) : (
                                    <Empty description={t('No location data yet')} />
                                )}
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card title={t('Geolocation distribution')} size="small">
                                {stats.geoDistribution.length > 0 ? (
                                    <Table
                                        columns={geoColumns}
                                        dataSource={stats.geoDistribution}
                                        rowKey="country"
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description={t('No data yet')} />
                                )}
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card title={t('Response status distribution')} size="small">
                                {rcodeData.length > 0 ? (
                                    <Table
                                        columns={rcodeColumns}
                                        dataSource={rcodeData}
                                        rowKey="rcode"
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description={t('No data yet')} />
                                )}
                            </Card>
                        </Col>
                    </Row>
                </Spin>
            </Card>
        </div>
    );
};

export default DnsAnalytics;
